import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useJobs, useLogout, usePatchMe } from '../api/hooks'
import type { Me } from '../api/types'
import { Modal } from './Modal'
import { HelpPopover } from './HelpPopover'

export function AppHeader({ me }: { me: Me }) {
  const logout = useLogout()
  const jobs = useJobs()
  const location = useLocation()
  const pending = (jobs.data?.counts.pending ?? 0) + (jobs.data?.counts.running ?? 0)
  const [editingName, setEditingName] = useState(false)

  return (
    <header className="flex h-11 shrink-0 items-center gap-3 border-b border-ink-200 bg-white px-3">
      <Link to="/" className="flex items-baseline gap-1.5">
        <span className="text-sm font-bold tracking-tight text-ink-900">loco</span>
        <span className="text-2xs uppercase tracking-widest text-ink-600">translations</span>
      </Link>
      <nav className="flex items-center gap-1 text-xs" aria-label="Main">
        <Link
          to="/"
          className={`rounded px-2 py-1 ${location.pathname === '/' ? 'bg-ink-100 font-semibold text-ink-900' : 'text-ink-600 hover:bg-ink-50'}`}
        >
          Dashboard
        </Link>
        <Link
          to="/jobs"
          className={`rounded px-2 py-1 ${location.pathname === '/jobs' ? 'bg-ink-100 font-semibold text-ink-900' : 'text-ink-600 hover:bg-ink-50'}`}
        >
          Jobs
        </Link>
        <Link
          to="/activity"
          className={`rounded px-2 py-1 ${location.pathname === '/activity' ? 'bg-ink-100 font-semibold text-ink-900' : 'text-ink-600 hover:bg-ink-50'}`}
        >
          Activity
        </Link>
        {/* Not CSS-hidden: for an editor this element is simply never created,
            so it can never appear in the DOM regardless of styling. */}
        {me.role === 'admin' && (
          <Link
            to="/settings"
            className={`rounded px-2 py-1 ${location.pathname.startsWith('/settings') ? 'bg-ink-100 font-semibold text-ink-900' : 'text-ink-600 hover:bg-ink-50'}`}
          >
            Settings
          </Link>
        )}
      </nav>
      <div className="flex-1" />
      <span
        className="chip border-ink-200 bg-ink-50 text-ink-600"
        title="AI translation jobs queued or running"
      >
        <span aria-hidden="true">◆</span> {pending} AI job{pending === 1 ? '' : 's'} pending
      </span>
      <HelpPopover />
      <button
        className="text-2xs text-ink-600 underline decoration-dotted hover:text-ink-900"
        title={me.name ? `${me.email} — click to change your name` : 'Click to set your name'}
        onClick={() => setEditingName(true)}
      >
        {me.name || me.email} · {me.role}
      </button>
      <button className="btn" onClick={() => logout.mutate()} disabled={logout.isPending}>
        Sign out
      </button>
      {editingName && <EditNameDialog me={me} onClose={() => setEditingName(false)} />}
    </header>
  )
}

function EditNameDialog({ me, onClose }: { me: Me; onClose: () => void }) {
  const patchMe = usePatchMe()
  const [name, setName] = useState(me.name)

  const submit = () => {
    patchMe.mutate(name.trim(), { onSuccess: onClose })
  }

  return (
    <Modal
      title="Your name"
      description="Shown on the activity feed instead of your email. Leave blank to show your email."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={patchMe.isPending} onClick={submit}>
            {patchMe.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div>
        <label className="label" htmlFor="my-name">
          Name
        </label>
        <input
          id="my-name"
          className="field mt-1"
          value={name}
          maxLength={200}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit()
          }}
        />
        {patchMe.isError && (
          <p role="alert" className="mt-2 rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(patchMe.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}
